import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  offerApi,
  UpdateOfferRequest,
  AcceptedInquiryAlignment,
} from '@texas/api/endpoints/offerApi';
import { currenciesApi } from '@texas/api/endpoints/currenciesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { CurrencyRate } from '@texas/components/shared/CurrencyRate';
import { TexasInputWrapper } from '@texas/components/shared/TexasInputWrapper';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { Locale } from '@texas/i18n/types';
import { exchangeRateCalculation } from '@texas/utils/helpers/numberHelpers';

interface Props {
  offerId: number;
  onChanged: () => void;
  mode: 'master' | 'child' | 'readonly';
  isDefaultCurrency?: boolean;
  matchedInquiry?: AcceptedInquiryAlignment | null;
  customerId?: number;
}

export function OfferDetail({
  offerId,
  onChanged,
  mode,
  isDefaultCurrency,
  matchedInquiry,
  customerId,
}: Props) {
  const { t } = useTranslation();
  const {
    useCurrenciesData: { findCurrency },
    currenciesData: [, refetchCurrencies],
  } = useMetadata();

  const { data, loading, error, refetch } = useApiResource(offerApi.getOffer);
  const {
    request: updateRequest,
    loading: updating,
    error: updateError,
  } = useApiRequest(offerApi.updateOffer);
  const { register, handleSubmit, reset, watch, setValue, formState } =
    useForm<UpdateOfferRequest>();
  const {
    data: customerCurrencies,
    loading: customerCurrenciesLoading,
    refetch: refetchCustomerCurrencies,
  } = useApiResource(
    currenciesApi.getAllCurrenciesMergedWithCustomerCurrencies,
  );

  useEffect(() => {
    refetchCurrencies();
  }, [refetchCurrencies]);

  useEffect(() => {
    refetch(offerId);
  }, [refetch, offerId]);

  useEffect(() => {
    if (customerId != null) {
      refetchCustomerCurrencies(customerId);
    }
  }, [customerId, refetchCustomerCurrencies]);

  useEffect(() => {
    if (data) {
      reset({
        exchangeRate: data.exchangeRate,
        sampleCharge: data.sampleCharge,
        mouldCharge: data.mouldCharge,
        surcharge: data.surcharge,
        supplierContactId: data.supplierContactId ?? null,
        mpq: data.mpq ?? null,
      });
    }
  }, [data, reset]);

  if (loading) return <Spinner size="sm" />;
  if (error) return <ErrorDetails error={error} />;
  if (!data) return null;

  const onSubmit = async (formData: UpdateOfferRequest) => {
    const payload: UpdateOfferRequest = isDefaultCurrency
      ? { ...formData, exchangeRate: data.exchangeRate }
      : formData;
    const res = await updateRequest(offerId, payload);
    if (!res.hasError) {
      refetch(offerId);
      onChanged();
    }
  };

  const currencyCode = findCurrency(data.currencyId)?.code ?? '';
  const selectedCurrency = findCurrency(data.currencyId);
  const customerCurrency = customerCurrencies?.find(
    (currency) =>
      currency.id === data.currencyId && currency.branchId === data.branchId,
  );
  const exchangeRate = watch('exchangeRate');
  const handleRefreshRate = (value: number) => {
    setValue('exchangeRate', value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  const convertInquiryValue = (value: number) =>
    exchangeRateCalculation(
      value,
      matchedInquiry ? matchedInquiry.exchangeRate : 1,
      exchangeRate,
      5,
    );
  const sortedTiers = [...data.tierPrices].sort(
    (a, b) => a.quantity - b.quantity,
  );

  const metaBadges = (
    <Flex gap={2} wrap="wrap" mb={3}>
      <Text fontSize="sm" color="gray.500">
        {formatDate(Locale.En, data.created)}
      </Text>
      {data.tierPrices.length > 0 &&
        data.tierPrices.every((tp) => tp.acceptedDate) && (
          <Badge colorScheme="green">{t('offer.ready')}</Badge>
        )}
      {data.archived && (
        <Badge colorScheme="orange">{t('inquiry.archived')}</Badge>
      )}
    </Flex>
  );

  if (mode === 'child' || mode === 'readonly') {
    return (
      <Box>
        <ErrorDetails error={updateError} />
        {metaBadges}
        <Flex gap={4} wrap="wrap" mb={4} direction="column">
          <ReadonlyRow label={t('inquiry.currency')} value={currencyCode} />
          <ReadonlyRow
            label={t('inquiry.exchangeRate')}
            value={String(data.exchangeRate)}
          />
          <ReadonlyRow
            label={t('inquiry.sampleCharge')}
            value={`${data.sampleCharge} ${currencyCode}`}
          />
          <ReadonlyRow
            label={t('inquiry.mouldCharge')}
            value={`${data.mouldCharge} ${currencyCode}`}
          />
          <ReadonlyRow
            label={t('inquiry.surcharge')}
            value={`${data.surcharge} ${currencyCode}`}
          />
          <ReadonlyRow
            label={t('inquiry.mpq')}
            value={data.mpq !== null ? String(data.mpq) : '—'}
          />
        </Flex>
        <Box>
          <Text fontWeight="semibold" mb={2}>
            {t('inquiry.tierPrices')}
          </Text>
          <TableContainer>
            <Table variant="configuration" size="sm">
              <Thead>
                <Tr>
                  <Th>{t('inquiry.quantity')}</Th>
                  <Th>{t('inquiry.price')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedTiers.map((tp) => (
                  <Tr key={tp.id}>
                    <Td>{tp.quantity}</Td>
                    <Td>
                      {tp.price !== null ? `${tp.price} ${currencyCode}` : '—'}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {metaBadges}

      <Flex gap={4} wrap="wrap" mb={4}>
        <ReadonlyRow label={t('inquiry.currency')} value={currencyCode} />
        <TexasInputWrapper
          label={t('inquiry.exchangeRate')}
          formControlProps={{ width: 'fit-content' }}
          error={formState.errors.exchangeRate}
          utilButtons={
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                variant="ghost"
                isDisabled={
                  isDefaultCurrency === true || selectedCurrency == null
                }
                isLoading={customerCurrenciesLoading}
              >
                {t('variant.supplier.refreshRate')}
              </MenuButton>
              <MenuList>
                {customerCurrency?.sellingRateModified && (
                  <MenuItem
                    onClick={() =>
                      handleRefreshRate(customerCurrency.sellingRate)
                    }
                    fontSize="sm"
                    isDisabled={exchangeRate === customerCurrency.sellingRate}
                  >
                    {t('variant.supplier.pullFromCustomer')} (
                    {customerCurrency.sellingRate})
                  </MenuItem>
                )}
                {selectedCurrency && (
                  <MenuItem
                    onClick={() =>
                      handleRefreshRate(selectedCurrency.sellingRate)
                    }
                    fontSize="sm"
                    isDisabled={exchangeRate === selectedCurrency.sellingRate}
                  >
                    {t('variant.supplier.pullFromBranch')} (
                    {selectedCurrency.sellingRate})
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          }
        >
          <Input
            variant="texas"
            type="number"
            isDisabled={isDefaultCurrency}
            {...register('exchangeRate', {
              valueAsNumber: true,
              required: t('variant.supplier.exchangeRateRequired'),
              validate: (value) =>
                value > 0
                  ? true
                  : t('variant.supplier.exchangeRateGreaterThanZero'),
            })}
          />
        </TexasInputWrapper>
        {isDefaultCurrency && (
          <Text fontSize="xs" color="gray.400" alignSelf="flex-end" mb={2}>
            {t('variant.supplier.branchDefaultCurrencyLocked')}
          </Text>
        )}
        {!isDefaultCurrency && selectedCurrency && (
          <Box width="100%">
            <CurrencyRate
              branchId={data.branchId}
              currencyId={data.currencyId}
              exchangeRate={exchangeRate}
              exchangeRateType="sales"
              customerId={customerId}
            />
          </Box>
        )}
        <Box>
          <TexasInputWrapper
            label={t('inquiry.sampleCharge')}
            formControlProps={{ width: 'fit-content' }}
            rightAddon={currencyCode}
            bottomLabel={
              matchedInquiry
                ? `${convertInquiryValue(
                    matchedInquiry.sampleCharge,
                  )} ${currencyCode}`
                : undefined
            }
          >
            <Input
              variant="texas"
              type="number"
              {...register('sampleCharge', { valueAsNumber: true })}
            />
          </TexasInputWrapper>
        </Box>
        <Box>
          <TexasInputWrapper
            label={t('inquiry.mouldCharge')}
            formControlProps={{ width: 'fit-content' }}
            rightAddon={currencyCode}
            bottomLabel={
              matchedInquiry
                ? `${convertInquiryValue(
                    matchedInquiry.mouldCharge,
                  )} ${currencyCode}`
                : undefined
            }
          >
            <Input
              variant="texas"
              type="number"
              {...register('mouldCharge', { valueAsNumber: true })}
            />
          </TexasInputWrapper>
        </Box>
        <Box>
          <TexasInputWrapper
            label={t('inquiry.surcharge')}
            formControlProps={{ width: 'fit-content' }}
            rightAddon={currencyCode}
            bottomLabel={
              matchedInquiry
                ? `${convertInquiryValue(
                    matchedInquiry.surcharge,
                  )} ${currencyCode}`
                : undefined
            }
          >
            <Input
              variant="texas"
              type="number"
              {...register('surcharge', { valueAsNumber: true })}
            />
          </TexasInputWrapper>
        </Box>
        <TexasInputWrapper
          label={t('inquiry.mpq')}
          formControlProps={{ width: 'fit-content' }}
        >
          <Input
            variant="texas"
            type="number"
            placeholder="—"
            {...register('mpq', {
              setValueAs: (v) => (v === '' ? null : parseInt(v, 10)),
            })}
          />
        </TexasInputWrapper>
      </Flex>

      <Button
        size="sm"
        variant="texas-solid"
        isLoading={updating}
        onClick={handleSubmit(onSubmit)}
        mb={4}
      >
        {t('general.save')}
      </Button>
    </Box>
  );
}

interface ReadonlyRowProps {
  label: string;
  value: string;
}

const ReadonlyRow = ({ label, value }: ReadonlyRowProps) => (
  <Flex gap={2} align="baseline">
    <Text fontSize="sm" color="gray.400" minW="140px">
      {label}
    </Text>
    <Text fontSize="sm">{value}</Text>
  </Flex>
);
